import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Download } from 'lucide-react';
import { Payroll, Employee } from './hooks/usePayrollData';
import { formatCurrency } from './utils/payrollUtils';
import { months, statusColors, statusLabels } from './utils/payrollConstants';

interface PayrollSlipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payroll: Payroll | null;
  employee: Employee | null;
  getPositionName: (posId: number | string) => string;
}

export const PayrollSlipModal: React.FC<PayrollSlipModalProps> = ({
  open,
  onOpenChange,
  payroll,
  employee,
  getPositionName,
}) => {
  if (!payroll || !employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Slip Gaji</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 p-6 bg-white" id="payslip">
          {/* Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-2xl font-bold">SLIP GAJI</h2>
            <p className="text-sm text-gray-500 mt-1">
              Periode: {months.find(m => m.value === payroll.period_month)?.label}{' '}
              {payroll.period_year}
            </p>
          </div>

          {/* Employee Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nama Pegawai</p>
              <p className="font-semibold">{employee.full_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">NIP</p>
              <p className="font-semibold">{employee.employee_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Jabatan</p>
              <p className="font-semibold">{getPositionName(employee.position_id || '')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge className={statusColors[payroll.status] || statusColors.draft}>
                {statusLabels[payroll.status] || payroll.status}
              </Badge>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left font-semibold">Komponen</th>
                  <th className="p-3 text-right font-semibold">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3">Gaji Pokok</td>
                  <td className="p-3 text-right font-mono">
                    {formatCurrency(payroll.base_salary)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Tunjangan Tetap</td>
                  <td className="p-3 text-right font-mono">{formatCurrency(payroll.allowances)}</td>
                </tr>
                <tr className="border-b bg-orange-50">
                  <td className="p-3 font-medium">Lembur ({payroll.overtime_hours || 0} jam)</td>
                  <td className="p-3 text-right font-mono font-semibold text-orange-600">
                    {formatCurrency(payroll.overtime_pay)}
                  </td>
                </tr>
                <tr className="border-b bg-purple-50">
                  <td className="p-3 font-medium">Bonus KPI (Score: {payroll.kpi_score || 0})</td>
                  <td className="p-3 text-right font-mono font-semibold text-purple-600">
                    {formatCurrency(payroll.kpi_bonus)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3">Bonus Lainnya</td>
                  <td className="p-3 text-right font-mono">{formatCurrency(payroll.bonus)}</td>
                </tr>
                <tr className="border-b bg-green-100">
                  <td className="p-3 font-bold">TOTAL PENDAPATAN</td>
                  <td className="p-3 text-right font-mono font-bold text-green-700">
                    {formatCurrency(payroll.gross_salary)}
                  </td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-3 font-semibold text-red-700">POTONGAN</td>
                  <td className="p-3"></td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6">Pajak (PPh 21)</td>
                  <td className="p-3 text-right font-mono text-red-600">
                    -{formatCurrency(payroll.tax)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6">BPJS Kesehatan</td>
                  <td className="p-3 text-right font-mono text-red-600">
                    -{formatCurrency(payroll.bpjs_employee)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 pl-6">Potongan Lainnya</td>
                  <td className="p-3 text-right font-mono text-red-600">
                    -{formatCurrency(payroll.deductions)}
                  </td>
                </tr>
                <tr className="bg-emerald-100">
                  <td className="p-4 font-bold text-base">GAJI BERSIH (TAKE HOME PAY)</td>
                  <td className="p-4 text-right font-mono font-bold text-lg text-emerald-700">
                    {formatCurrency(payroll.net_salary)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-500 pt-4 border-t">
            <p>Slip gaji ini dibuat secara otomatis oleh sistem</p>
            <p className="mt-1">Dicetak pada: {new Date().toLocaleDateString('id-ID')}</p>
          </div>

          {/* Print Button */}
          <div className="flex justify-center gap-3">
            <Button
              onClick={() => window.print()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Print / Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
